/**
 * PII Simulation Tab — Phase 1 of PLAN-pii-simulation-and-metrics.md.
 *
 * Paste text → see scan matches → see the tokenised view the agent would
 * get → see the detokenised view the caller gets back. LLM mutation presets
 * exercise the detokeniser's 6 repair patterns so you can verify they still
 * work after pattern-rule changes.
 */
import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  PlayIcon,
  ClipboardDocumentIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import clsx from 'clsx'
import toast from 'react-hot-toast'

type MutationPreset = 'none' | 'case_flip' | 'add_spacing' | 'broken_guillemets' | 'custom'

const CATEGORY_COLORS: Record<string, string> = {
  SSN: '#ef4444',
  EMAIL: '#3b82f6',
  PHONE_US: '#f59e0b',
  PHONE_UK: '#f59e0b',
  PHONE_INTL: '#f59e0b',
  CREDIT_CARD: '#dc2626',
  IBAN: '#8b5cf6',
  NHS_NUMBER: '#06b6d4',
  PASSPORT: '#10b981',
  DL_NUMBER: '#14b8a6',
  DOB: '#6366f1',
  ADDRESS: '#84cc16',
  POSTCODE_UK: '#84cc16',
  IP_ADDRESS: '#f97316',
  PERSON: '#ec4899',
  ORG: '#8b5cf6',
  LOCATION: '#22c55e',
}

function colorFor(cat: string): string {
  return CATEGORY_COLORS[cat] || '#6366f1'
}

interface Match {
  category: string
  start: number
  end: number
  confidence: number
  tier: number
  pattern_name: string
  value_masked: string
}

function HighlightedOriginal({ text, matches }: { text: string; matches: Match[] }) {
  if (!matches.length) return <span className="whitespace-pre-wrap">{text || <em className="text-muted-foreground">(empty)</em>}</span>
  const sorted = [...matches].sort((a, b) => a.start - b.start)
  const parts: React.ReactNode[] = []
  let cursor = 0
  sorted.forEach((m, i) => {
    if (m.start > cursor) parts.push(<span key={`t-${i}`}>{text.slice(cursor, m.start)}</span>)
    const color = colorFor(m.category)
    parts.push(
      <span
        key={`m-${i}`}
        className="px-0.5 rounded font-semibold"
        style={{ backgroundColor: `${color}25`, color, textDecorationLine: 'underline', textDecorationStyle: 'wavy' }}
        title={`${m.category} · ${(m.confidence * 100).toFixed(0)}% · T${m.tier} · ${m.pattern_name}`}
      >
        {text.slice(m.start, m.end)}
      </span>
    )
    cursor = m.end
  })
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>)
  return <span className="whitespace-pre-wrap">{parts}</span>
}

function HighlightedTokens({ text }: { text: string }) {
  if (!text) return <em className="text-muted-foreground">(empty)</em>
  const parts: React.ReactNode[] = []
  const regex = /<{1,2}\s*([A-Za-z_]+_\d+)\s*>{1,2}|«\s*([A-Za-z_]+_\d+)\s*»/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={`t-${i}`}>{text.slice(last, m.index)}</span>)
    const name = m[1] || m[2] || m[0]
    const cat = name.split('_').slice(0, -1).join('_')
    const color = colorFor(cat)
    parts.push(
      <span key={`tok-${i}`} className="px-1 py-0.5 rounded font-mono text-[11px] font-semibold"
        style={{ backgroundColor: `${color}25`, color, border: `1px dashed ${color}80` }}>
        {m[0]}
      </span>
    )
    last = m.index + m[0].length
    i++
  }
  if (last < text.length) parts.push(<span key="tail">{text.slice(last)}</span>)
  return <span className="whitespace-pre-wrap">{parts}</span>
}

const RECENT_KEY = 'pii-sim-recent'
const FAVS_KEY = 'pii-sim-favs'

interface RecentEntry {
  text: string
  preset: MutationPreset
  custom?: string
  ts: number
  matches?: number
}

function loadJson<T>(key: string, fb: T): T {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb } catch { return fb }
}
function saveJson(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)) } catch { /* ignore */ }
}

const PIISimulationTab: React.FC = () => {
  const [text, setText] = useState('')
  const [preset, setPreset] = useState<MutationPreset>('none')
  const [customMutation, setCustomMutation] = useState('')
  const [recents, setRecents] = useState<RecentEntry[]>(() => loadJson(RECENT_KEY, []))
  const [favs, setFavs] = useState<RecentEntry[]>(() => loadJson(FAVS_KEY, []))

  const sim = useMutation({
    mutationFn: async () => {
      return apiClient.previewPII({
        text,
        mutation_preset: preset,
        simulate_llm_output: preset === 'custom' ? customMutation : undefined,
      })
    },
    onSuccess: (data) => {
      const entry: RecentEntry = {
        text, preset, custom: preset === 'custom' ? customMutation : undefined,
        ts: Date.now(), matches: data.scan_matches.length,
      }
      const next = [entry, ...recents.filter(r => r.text !== text || r.preset !== preset)].slice(0, 10)
      setRecents(next); saveJson(RECENT_KEY, next)
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || err?.message || 'Simulation failed'),
  })

  const effectiveResult = sim.data

  const handleRun = () => {
    if (!text.trim()) { toast.error('Enter text first'); return }
    sim.mutate()
  }

  const handleReplay = (r: RecentEntry) => {
    setText(r.text); setPreset(r.preset); if (r.custom) setCustomMutation(r.custom)
  }

  const toggleFav = (r: RecentEntry) => {
    const exists = favs.some(f => f.text === r.text && f.preset === r.preset)
    const next = exists ? favs.filter(f => !(f.text === r.text && f.preset === r.preset))
                        : [r, ...favs].slice(0, 20)
    setFavs(next); saveJson(FAVS_KEY, next)
  }
  const isFav = (r: RecentEntry) => favs.some(f => f.text === r.text && f.preset === r.preset)

  const copyToClipboard = (s: string, label: string) => {
    navigator.clipboard.writeText(s).then(() => toast.success(`${label} copied`))
  }

  return (
    <div className="space-y-4">
      {/* Input + controls */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
          <h3 className="text-base font-semibold text-foreground">PII Simulation</h3>
          <span className="ml-auto text-xs text-muted-foreground">
            End-to-end tokenise → detokenise without agent execution
          </span>
        </div>

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          placeholder="Paste text with PII — e.g. 'My SSN is 123-45-6789 and my email is jane@example.com'"
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-card font-mono resize-y"
        />

        <div className="flex items-end gap-3 flex-wrap">
          <div className="w-44">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Mock LLM mutation</label>
            <select value={preset} onChange={e => setPreset(e.target.value as MutationPreset)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card">
              <option value="none">None (echo tokens)</option>
              <option value="case_flip">Case flip (lowercase)</option>
              <option value="add_spacing">Add spacing</option>
              <option value="broken_guillemets">Broken guillemets (« »)</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {preset === 'custom' && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                Custom LLM response (reference tokens like <code>&lt;&lt;SSN_1&gt;&gt;</code>)
              </label>
              <input type="text" value={customMutation} onChange={e => setCustomMutation(e.target.value)}
                placeholder="The patient <<SSN_1>> was referred by <<PERSON_1>>..."
                className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-card font-mono" />
            </div>
          )}
          <Button onClick={handleRun} disabled={sim.isPending || !text.trim()}>
            {sim.isPending ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />}
            <span className="ml-1.5">{sim.isPending ? 'Simulating…' : 'Simulate'}</span>
          </Button>
        </div>
      </Card>

      {/* Result */}
      {effectiveResult && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Original */}
            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Original (sent to proxy)</p>
                <span className="text-[10px] text-muted-foreground">{effectiveResult.original_text.length} chars</span>
              </div>
              <div className="text-xs bg-muted/30 rounded p-2 font-mono min-h-[4rem] max-h-64 overflow-y-auto">
                <HighlightedOriginal text={effectiveResult.original_text} matches={effectiveResult.scan_matches} />
              </div>
            </Card>

            {/* Tokenised */}
            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Tokenised (what agent sees)</p>
                <button onClick={() => copyToClipboard(effectiveResult.tokenised_text, 'Tokenised')}
                  className="text-muted-foreground hover:text-foreground">
                  <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-xs bg-muted/30 rounded p-2 font-mono min-h-[4rem] max-h-64 overflow-y-auto">
                <HighlightedTokens text={effectiveResult.tokenised_text} />
              </div>
            </Card>

            {/* Restored */}
            <Card className="p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Restored (returned to caller)</p>
                <button onClick={() => copyToClipboard(effectiveResult.restored_text, 'Restored')}
                  className="text-muted-foreground hover:text-foreground">
                  <ClipboardDocumentIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="text-xs bg-muted/30 rounded p-2 font-mono min-h-[4rem] max-h-64 overflow-y-auto">
                <HighlightedOriginal text={effectiveResult.restored_text} matches={effectiveResult.scan_matches} />
              </div>
              {effectiveResult.tokens_unresolved > 0 && (
                <p className="text-[10px] text-amber-600 mt-1">
                  ⚠ {effectiveResult.tokens_unresolved} unresolved token{effectiveResult.tokens_unresolved === 1 ? '' : 's'}
                </p>
              )}
              {effectiveResult.mutations_repaired > 0 && (
                <p className="text-[10px] text-emerald-600 mt-1">
                  ✓ {effectiveResult.mutations_repaired} mutation{effectiveResult.mutations_repaired === 1 ? '' : 's'} repaired
                </p>
              )}
            </Card>
          </div>

          {/* Metrics + category legend */}
          <Card className="p-3">
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span>
                Detections:{' '}
                {effectiveResult.scan_matches.length === 0 ? (
                  <em>none</em>
                ) : (
                  effectiveResult.scan_matches.slice(0, 10).map((m, i) => (
                    <Badge key={i} variant="gray" size="sm" className="ml-1"
                      style={{ color: colorFor(m.category), borderColor: `${colorFor(m.category)}60` } as any}>
                      {m.category} ({(m.confidence * 100).toFixed(0)}%)
                    </Badge>
                  ))
                )}
              </span>
              <span>scan {effectiveResult.scan_duration_ms.toFixed(2)}ms</span>
              <span>tokenise {effectiveResult.tokenise_duration_ms.toFixed(2)}ms</span>
              <span>detokenise {effectiveResult.detokenise_duration_ms.toFixed(2)}ms</span>
              <span>inflation {effectiveResult.token_inflation_pct >= 0 ? '+' : ''}{effectiveResult.token_inflation_pct.toFixed(1)}%</span>
              <Badge variant="gray" size="sm" title="Pattern rule set version">v{effectiveResult.pattern_version}</Badge>
              <code className="text-[10px]">{effectiveResult.session_id}</code>
            </div>
          </Card>
        </div>
      )}

      {/* Recent + Favourites */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SimList title="Recent" items={recents} onReplay={handleReplay} onToggleFav={toggleFav} isFav={isFav}
          onClear={() => { setRecents([]); saveJson(RECENT_KEY, []) }} />
        <SimList title="Favourites" items={favs} onReplay={handleReplay} onToggleFav={toggleFav} isFav={isFav}
          onClear={() => { setFavs([]); saveJson(FAVS_KEY, []) }} />
      </div>
    </div>
  )
}

interface SimListProps {
  title: string
  items: RecentEntry[]
  onReplay: (r: RecentEntry) => void
  onToggleFav: (r: RecentEntry) => void
  isFav: (r: RecentEntry) => boolean
  onClear: () => void
}

function SimList({ title, items, onReplay, onToggleFav, isFav, onClear }: SimListProps) {
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{title}</p>
        {items.length > 0 && (
          <button onClick={onClear} className="text-[10px] text-muted-foreground hover:text-red-500 inline-flex items-center gap-0.5">
            <TrashIcon className="w-3 h-3" /> clear
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">No entries yet</p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {items.map((r, i) => (
            <li key={i} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/30 text-xs">
              <span className="text-[10px] uppercase font-semibold text-blue-500 w-16 shrink-0">{r.preset}</span>
              <button onClick={() => onReplay(r)} className="flex-1 text-left text-foreground truncate hover:underline font-mono">
                {r.text.slice(0, 80)}{r.text.length > 80 && '…'}
              </button>
              {r.matches != null && (
                <span className="text-[10px] text-muted-foreground tabular-nums">{r.matches} hits</span>
              )}
              <button onClick={() => onToggleFav(r)} className="text-muted-foreground hover:text-amber-500 shrink-0">
                <StarIcon className={clsx('w-3.5 h-3.5', isFav(r) && 'fill-amber-500 text-amber-500')} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export default PIISimulationTab
